import React, { useEffect, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import css from "../../../../../../common/chartMagneticStyle.less";
import NoDataAvailable from '../../../../../../common/NoDataAvailable';
import { am4core, am4charts } from "../../../../../../loaders/amchartsLoader";
import { colors, anomalyColors, statusColors } from "../../../../../../utils/colors";
import { charRect, dashedLineChar } from "../../../../../../utils/enums";
import { formatBytes } from "../../../../../../utils/format";
import { exportCSV } from "../../../../../../utils/chart";
import { addUnitsFromDate, shortDisplayTime } from "../../../../../../utils/displayTime"; 
import { formatDateInAMPM, setZoom, tooltipStyling,
    getScrollRange, setScrollbarStyling, addCursor, customizeAnomalyLegend, customizeLegend, enableScrollbarGripKeyboard   } from "../../common/adUtils";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import useI18n from '../../../../../../i18n/useI18n';
import { initChartThemeSupport, isDarkModeActive } from "../../../../../../common/Theme";
import { getCssVariableColor } from '../../../../../../utils/chart';


let chartColors = {
    default: colors.lavendar60,
    Baseline: anomalyColors.Baseline,
};

const [HbrIconButton, HbrTooltip] = reactWrapper(["hbr-icon-button", "hbr-tooltip"]);

const ADLineChart = (props) => {
    const { tViews } = useI18n('views')
    const currentChartRef = useRef();
    const [chartLossData, setLossData] = useState();
    const [chartJitterData, setJitterData] = useState();
    const [chartLatencyData, setLatencyData] = useState();
    const [chartUsageData, setUsageData] = useState();
    const [allCharts, setAllCharts] = useState([]);
    const [isScrollBar, setScrollBar] = useState(null);
    const [isZoomed, setisZoomed] = useState(null);
    const [timeStampLabel, setTimeStampLabel] = useState();

    const getCustomSpace = (name) => {
        let space = `${tViews('preSpace')}`
        if(name !== 'Baseline') {
            let count = 10 - name.length
            while(count > 0) {
                space += `${tViews('preSpace')}`
                count--
            }
        }
        return space
    }

    const createTipText = (name, val, context = '', units = '') => {
        if(name !== "Usage") {
            return `[${chartColors['default']}]${charRect}[/] ${tViews('preSpace')} ${name}: ${getCustomSpace(name)} [bold]${val}${units}[/]\n
            [${chartColors['Baseline']}]${dashedLineChar}[/] ${tViews('preSpace')} ${tViews('predictiveNetworks.baselinewithColon')} ${getCustomSpace('Baseline')} [bold]${context['Baseline']}${units}[/]\n`;
        } else {
            return `[${chartColors['default']}]${charRect}[/] ${tViews('preSpace')} ${name}: ${getCustomSpace(name)} [bold]${val}${units}[/]\n`
        }
    }

    const synchronizeTooltips = (charts) => {
        charts.forEach(sourceChart => {
            sourceChart?.cursor?.events.on("cursorpositionchanged", function () {
                const xAxis = sourceChart.xAxes.getIndex(0);
                const position = sourceChart.cursor.xPosition;
                const date = xAxis.positionToDate(position); // Get the date at the cursor position
                
                charts.forEach(targetChart => {
                    if (targetChart !== sourceChart) {
                        const targetXAxis = targetChart.xAxes.getIndex(0);
                        const targetPosition = targetXAxis.dateToPosition(date); // Convert the date to the target chart's position
    
                        // Move the cursor to the same position on the target chart
                        targetChart.cursor.triggerMove(
                            { x: targetPosition * targetChart.plotContainer.pixelWidth, y: 0 },
                            "soft"
                        );

                        // Show tooltips for all series at the same date
                        targetChart.series.each(series => {
                            let tooltipContent;
                            let dataItem = series?.tooltipDataItem?.dataContext;
                            if(dataItem) {
                                const tooltipTimestamp = formatDateInAMPM(dataItem.entry_ts);
                                tooltipContent = `${tViews('postSpace')}[bold]${tooltipTimestamp}[/]\n\n`;
                                if(series.name === "Usage") {
                                    tooltipContent += createTipText(series.name, formatBytes(dataItem[series.name]));
                                } else {
                                    let units = series.name === 'Loss' ? "%" : "ms";
                                    tooltipContent += createTipText(series.name, dataItem[series.name], dataItem, units);
                                }
                            }
                            

                            let matchingDataItem = null;
                            series.tooltipText = series.name !== "Baseline" && tooltipContent;
                            series.dataItems.each(dataItem => {
                                if (dataItem.dateX.getTime() === date.getTime()) {
                                    matchingDataItem = dataItem;
                                }
                            });
                        
                            if (matchingDataItem) {
                                series.tooltipDataItem = matchingDataItem; // Set the tooltip data item
                                series.showTooltipAtDataItem(matchingDataItem); // Show the tooltip at the data item
                            }
                        });
                    }
                });
            });

            sourceChart.plotContainer.events.on("out", function () {
                charts.forEach(chart => {
                    chart.series.each(series => {
                        series.hideTooltip();
                        series.tooltip.disabled = true;
                        setTimeout(() => {
                            series.tooltip.disabled = false; // Re-enable the tooltip after a short delay
                        }, 100);
                    });
                    chart.cursor.hide();
                    chart.tooltipContainer.children.clear();
                    chart.tooltipContainer.hideTooltip();
                });
            });
    
            // Hide tooltips on all charts when the cursor leaves the source chart
            sourceChart?.cursor?.events.on("cursorout", function () {
                charts.forEach(chart => {
                    chart.series.each(series => {
                        series.hideTooltip();
                    });
                    chart.cursor.hide();
                    chart.tooltipContainer.hideTooltip();
                });
            });

        });
    };

    const createChartAttr = (chart) => {
        chart.paddingRight = 10;
        chart.paddingBottom = 0;
        chart.paddingLeft = 0;
        chart.width = am4core.percent(97);

        return chart;
    }

    const createDataAxis = (chart) => {
        var dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.renderer.grid.template.location = 0;
        dateAxis.renderer.labels.template.location = 0.0001;
        dateAxis.startLocation = 0;
        dateAxis.endLocation = 1;
        dateAxis.baseInterval = { timeUnit: "minute", count: 5 };
        dateAxis.cursorTooltipEnabled = false;

        return dateAxis;
    }

    const createValueAxis = (chart, label) => {
        var valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.title.text = label;
        valueAxis.min = 0;
        valueAxis.renderer.minGridDistance = 50;
        valueAxis.cursorTooltipEnabled = false;
        valueAxis.extraMax = 0.1;
        valueAxis.adapter.add("max", (max, target) => {
            if(label === "Loss") {
                valueAxis.renderer.minGridDistance = 30;
                const data = target.chart.data;
                if (data && data.length > 0) {
                    const maxValue = Math.max(...data.map(item => item[label] || 0));
                    return Math.min(maxValue * 1.1, 100 * 1.1); // Add 10% padding to see anomaly detected
                }
                return max;
            }
            return max;
        });

        return valueAxis;
    }

    const createSeries = (chart, name) => {
        var series = chart.series.push(new am4charts.LineSeries());
        series.name = name;
        series.dataFields.dateX = "entry_ts";
        series.dataFields.valueY = name;
        series.connect = false;
        series.autoGapCount = 60;
        series.strokeWidth = 2;
        series.smoothing = "monotoneX";
        series.stroke = colors.lavendar60;
        series.showOnInit = false;

        if(name === "Baseline") {
            series.strokeDasharray = "4,2";
            series.strokeOpacity = 0.2;
            series.fillOpacity = 0.2;
            series.fill = am4core.color(colors.gray80);
        }

        let tooltip = new am4core.Tooltip();
        tooltipStyling(tooltip);
        series.tooltip = tooltip;

        return series;
    }

    const createScrollbar = (chart, scrollBar) => {
        chart.events.on("ready", () => {
            chart.series.each(series => scrollBar?.series?.push(series));
            var scrollSeries = chart.scrollbarX?.scrollbarChart?.series?.getIndex(0);
            scrollSeries.strokeDasharray = "2,4";
        })
    }

    const createChart = (id, data) => {
        am4core.options.autoDispose = true;
        var chart = am4core.create(id, am4charts.XYChart);
        chart.data = data || [];
        chart.height = 250;
        chart.id = id

        createChartAttr(chart);
        if(chart.id !== "Usage") chart.paddingLeft = 20;
        let dataAxisTrend = createDataAxis(chart);

        if (props.selectedHour) {
            let event = dataAxisTrend.axisRanges.create();
            event.date = new Date(props.selectedHour);
            event.endDate = new Date(addUnitsFromDate(new Date(props.selectedHour), 1, "h"));
            event.grid.disabled = true;
            event.axisFill.fillOpacity = 0.1;
            event.axisFill.fill = am4core.color(colors.blue70);
        }

        createValueAxis(chart, id);
        addCursor(chart);
        setTimeStampLabel(shortDisplayTime(props.timePeriod?.[0]) + " - " + shortDisplayTime(props.timePeriod?.[1]));

        if(chart.id === "Usage") {
            chart.yAxes.values[0].numberFormatter.numberFormat = "#.0b";
        }

        // legends
        chart.legend = customizeLegend(am4charts, "top");

        const anomalyLegend = new am4charts.Legend ();
        anomalyLegend.data = [
        { name: tViews('predictiveNetworks.anomalyDetected'), fill: colors.gray40 }
        ];
        customizeAnomalyLegend(anomalyLegend, statusColors.poor, am4core);
        if(chart.id !== "Usage") {
            anomalyLegend.parent = chart.chartContainer;
            // Apply theme-aware text color to anomaly legend
            const textColor = isDarkModeActive() ? colors.gray100 : colors.gray15;
            anomalyLegend.labels.template.fill = am4core.color(textColor);
            // Store reference to anomaly legend for theme updates
            chart._anomalyLegend = anomalyLegend;
        }
        if(chart.id === "Loss") setLossData(chart);
        if(chart.id === "Jitter") setJitterData(chart);
        if(chart.id === "Latency") setLatencyData(chart);
        if(chart.id === "Usage") setUsageData(chart);
        initChartThemeSupport(chart, am4core, {id: chart.id});
        return chart;
    }

    useEffect(() => {
        const charts = [];
        props.config?.forEach(item => {
            if(item.data.length === 0) return;
            let chart = createChart(item.id, item.data);
            currentChartRef.current = chart;
            charts.push(chart);
            let trendList = Object.keys(item.data[0]);
            trendList.forEach(d => {
                if(d !== 'entry_ts' && d !== "anomaly" && d !== "sla" &&
                    d !== "std" && d !== "tooltip") {
                    createSeries(chart, d);
                }
            })

        });

        synchronizeTooltips(charts);
        setAllCharts(charts);
        return () => {
            charts.forEach(chart => {
                chart.dispose();
                if (chart.scrollbarX) {
                    chart.scrollbarX.dispose();
                }
            });
        }
    }, [props.config]);

    useEffect(() => {
        if (allCharts.length === 0) return;
        if(props.selectedHour) {
            var scrollBar = new am4charts.XYChartScrollbar();
            let scrollbarHostChart;
            allCharts.forEach(chart => {
                let series = chart.series.getIndex(0);
                // Scrollbar
                chart.scrollbarX = scrollBar;
                scrollBar.series?.push(series);
                createScrollbar(chart, scrollBar);
                if(chart.id === "Usage") {
                    chart.scrollbarX.parent = chart.bottomAxesContainer;
                    scrollbarHostChart = chart;
                }
            })
            setScrollbarStyling(scrollBar, { insertBeforeChart: false });
            if (scrollbarHostChart) {
                enableScrollbarGripKeyboard(scrollBar, {
                    container: scrollbarHostChart.htmlContainer,
                    insertBeforeChart: false,
                    labelPrefix:
                        scrollbarHostChart.yAxes.getIndex(0)?.title?.text || scrollbarHostChart.id
                });
            }
            setScrollBar(true);
        }
    }, [allCharts]);

    useEffect(() => {
        if (isScrollBar === true && props.selectedHour && allCharts?.length > 0) {
            allCharts.forEach(chart => {
                const xAxis = chart.xAxes.getIndex(0);
                xAxis.axisRanges.clear();

                let event = xAxis.axisRanges.create();
                event.date = new Date(props.selectedHour);
                event.endDate = new Date(addUnitsFromDate(new Date(props.selectedHour), 1, "h"));
                event.grid.disabled = true;
                event.axisFill.fillOpacity = 0.1;
                event.axisFill.fill = am4core.color(colors.blue70);
                
                // Adjust the scrollbar position according to selected hour
                let scrollRange = getScrollRange(props.selectedHour, chart);
                setZoom(chart, scrollRange.dateEnd, scrollRange.dateStart);
                setisZoomed(true);
            });
        }
    }, [isScrollBar, props.selectedHour]);

    useEffect(() => {
        if (isZoomed === true) {
            allCharts.forEach(chart => {
                chart.events.on("ready", () => {
                    chart.series.each(series => {
                        if( series.name !== "Baseline") {
                            const bullet = series.bullets.push(new am4charts.Bullet());
                            var arrow = bullet.createChild(am4core.Triangle);
                            arrow.width = 8;
                            arrow.height = 8;
                            arrow.horizontalCenter = "middle";
                            arrow.verticalCenter = "middle";
                            arrow.strokeWidth = 0;
                            arrow.fill = getCssVariableColor(statusColors.poor);
                            arrow.rotation = 180;
                            bullet.dy = -3;
                            bullet.adapter.add("fillOpacity", function(fillOpacity, target) {
                                var isAnomaly = target.dataItem?._dataContext?.anomaly;
                                return isAnomaly ? 1 : 0;
                            });
                        }
                    });
                });
            })
        }
    }, [isZoomed]);

    const onDownloadCsvClick = (id) => {  
        const dataMap = {
            Loss: chartLossData,
            Jitter: chartJitterData,
            Latency: chartLatencyData,
            Usage: chartUsageData,
        };
        const chartData = dataMap[id];
        if (chartData) {
            if (id === "Loss") {
                id = tViews('trendingAppsViewLoss');
            } else if (id === "Jitter") {
                id = tViews('trendingAppsViewJitter');
            } else if (id === "Latency") {
                id = tViews('trendingAppsViewLatency');
            } else if (id === "Usage") {
                id = tViews('trendingAppsViewBandwidth');
            }
            exportCSV(chartData, id);
        }
    };

    return (
        <div className='ss-trend-chart'>
            {props.config?.map((item, index) => (
                item.data.length > 0
                    ? <>
                        <div id="usage-based-ad-download-csv" key={index}>
                            <HbrTooltip content={tViews('downloadCSV')}>
                                <span>
                                    <HbrIconButton
                                        size="20px"
                                        name="file-csv"
                                        data-testid="DownloadCsvBtn"
                                        onClick={() => onDownloadCsvClick(item.id)}
                                        arialabel={tViews('downloadChartDisplaying') + item.id.toLowerCase() + tViews('downloadCSVInfo') + timeStampLabel}
                                    />
                                </span>
                            </HbrTooltip> 
                        </div>
                        <div
                            id={item.id}
                            key={index}
                            className={`${css.chart} hbr-type-body4`}
                            role="region"
                            aria-label={item.id}
                            tabIndex={0}/>
                    </>
                    : <NoDataAvailable key={index} />
            ))}
        </div>
    );
}

ADLineChart.propTypes = {
    config: PropTypes.array.isRequired,
    selectedHour: PropTypes.string,
    timePeriod: PropTypes.array
};

export default ADLineChart;
